import { forEach, isUndefined, map, padStart, size, times, toNumber, toString } from "lodash"
import { useMemo } from "react"
import {
  clampDate,
  DatetimePickerColumnType,
  DatetimePickerType,
  getDatetime,
  getEndDayOfMonth,
  MAX_DATE,
  MIN_DATE,
  useDatetimeRanges,
} from "./datetime-picker.shared"

interface UseDatetimePicker {
  type?: DatetimePickerType
  defaultValue?: Date
  value?: Date
  min?: Date
  max?: Date
  fields?: DatetimePickerColumnType[]

  filter?(type: DatetimePickerColumnType, values: string[]): string[]

  formatter?(type: DatetimePickerColumnType, value: string): string
}

const defaultFormatter = (type: DatetimePickerColumnType, value: string) => value

export function useDatetimePicker(options: UseDatetimePicker = {}) {
  const {
    defaultValue = undefined,
    value = undefined,
    min: minDate = MIN_DATE,
    max: maxDate = MAX_DATE,
    type = "datetime",
    fields = [],
    filter,
    formatter = defaultFormatter,
  } = options
  const clampDefaultValue = clampDate(defaultValue, minDate, maxDate)
  // When the defaultValue has value and the value is undefined,
  // set the value to defaultValue
  // The clampValue is value or defaultValue
  const clampValue = clampDate(value ?? defaultValue, minDate, maxDate)
  const ranges = useDatetimeRanges(clampValue, minDate, maxDate, type, fields)

  const columns = useMemo(
    () =>
      map(ranges, ({ type, range }) => {
        let values = times(range[1] - range[0] + 1, (index) =>
          padStart(`${range[0] + index}`, 2, "0"),
        )
        if (filter) {
          values = filter(type, values)
        }

        const children = map(values, (value) => ({
          value,
          children: formatter(type, value),
        }))

        return {
          type,
          value: type,
          children,
        }
      }),
    [filter, formatter, ranges],
  )

  function toDate(datetimeValue: string[]): Date {
    const date: Date = new Date(minDate.getTime())
    forEach(columns, ({ type }, index) => {
      switch (type) {
        case "year":
          if (size(datetimeValue) > index) {
            date.setFullYear(toNumber(datetimeValue[index]))
          }
          break
        case "month":
          if (size(datetimeValue) > index) {
            date.setMonth(toNumber(datetimeValue[index]) - 1)
          }
          break
        case "day":
          if (size(datetimeValue) > index) {
            const endDayOfMonth = getEndDayOfMonth(date.getFullYear(), date.getMonth() + 1)
            const day = toNumber(datetimeValue[index])
            date.setDate(day > endDayOfMonth ? endDayOfMonth : day)
          }
          break
        case "hour":
          if (size(datetimeValue) > index) {
            date.setHours(toNumber(datetimeValue[index]))
          }
          break
        case "minute":
          if (size(datetimeValue) > index) {
            date.setMinutes(toNumber(datetimeValue[index]))
          }
          break
        case "second":
          if (size(datetimeValue) > index) {
            date.setSeconds(toNumber(datetimeValue[index]))
          }
          break
      }
    })

    return clampDate(date, minDate, maxDate)
  }

  function toValue(date: Date | undefined) {
    if (isUndefined(date)) {
      return date
    }
    const [year, month, day, hour, minute, second] = getDatetime(date)
    return map(columns, (column) => {
      switch (column.type) {
        case "year":
          return toString(year)
        case "month":
          return padStart(toString(month), 2, "0")
        case "day":
          return padStart(toString(day), 2, "0")
        case "hour":
          return padStart(toString(hour), 2, "0")
        case "minute":
          return padStart(toString(minute), 2, "0")
        case "second":
          return padStart(toString(second), 2, "0")
        default:
          return ""
      }
    })
  }

  return {
    toDate,
    defaultValue: toValue(isUndefined(defaultValue) ? defaultValue : clampDefaultValue),
    value: toValue(isUndefined(value) ? value : clampValue),
    columns,
  }
}

export default useDatetimePicker
